import type { MedplumClient } from "@medplum/core";
import type { Coverage, Patient, Reference } from "@medplum/fhirtypes";
import {
  type CreateResourceOptions,
  FhirResource,
  type SearchOptions,
  type SubscriptionCallback,
} from "@vibrant/models/fhir/FhirResource";
import type { ResourceData } from "@vibrant/models/fhir/fhir-resource-types";
import { z } from "zod";
import { assertCoverageDatesInRange } from "@/lib/insurance/coverage-date-range";
import { createLogger } from "@/lib/logger";
import { time } from "@/lib/time";
import { getVibrantSuperOrgId } from "@/lib/utils";
import { searchResourcesByDocumentReference } from "@/models/source/document-source-lookup";
import { Source } from "@/models/source/Source";

const logger = createLogger("Insurance");

export interface InsuranceSearchQuery {
  memberId?: string;
  status?: Coverage["status"];
}

export interface InsuranceUpsertRequest {
  memberId: string;
  status: "active" | "draft";
  payorName: string;
  payorCode?: string;
  planName?: string;
  policyNumber: string;
  groupNumber?: string;
  startDate?: string;
  endDate?: string;
  documentReferenceId?: string;
  order?: number;
}

export interface InsuranceSaveRequest extends Omit<InsuranceUpsertRequest, "status"> {
  insuranceId?: string;
}

/**
 * Models the insurance for a member. Wraps the FHIR Coverage resource.
 */
export class Insurance extends FhirResource<Coverage> {
  static readonly ResourceType = "Coverage";

  static readonly Extension = {
    PayorCode: "vibrant.insurance.payor-code",
  } as const;

  static readonly Schema = {
    payorName: z.string().min(1),
    planName: z.string().optional(),
    policyNumber: z.string().min(1),
    groupNumber: z.string().optional(),
    startDate: z.union([z.string().length(0), z.string().date()]).optional(),
    endDate: z.union([z.string().length(0), z.string().date()]).optional(),
  };
  static readonly Identifier = {
    DocumentReference: "Insurance.documentReference",
    PolicyNumber: "vibrant.insurance.policy-number",
  } as const;

  //------------------------------------------------------------------------------------------------
  //                                         GETTERS
  //------------------------------------------------------------------------------------------------
  get id(): string {
    return this.resource.id!;
  }

  get subscriberId(): string | undefined {
    return this.resource.subscriberId;
  }

  get planName(): string | undefined {
    return this.resource.class?.find((c) => c.type.coding?.[0]?.code === "plan")?.name;
  }

  get groupNumber(): string | undefined {
    return this.resource.class?.find((c) => c.type.coding?.[0]?.code === "group")?.value;
  }

  get policyNumber(): string | undefined {
    const systemValue = this.resource.identifier?.find(
      (id) => id.system === Insurance.Identifier.PolicyNumber,
    )?.value;
    if (systemValue) {
      return systemValue;
    }

    return this.resource.identifier?.find(
      (id) =>
        id.type?.coding?.[0]?.code === "MB" &&
        id.type?.coding?.[0]?.system === "http://terminology.hl7.org/CodeSystem/v2-0203",
    )?.value;
  }

  get payorName(): string | undefined {
    return this.resource.payor?.[0]?.display;
  }

  get payorCode(): string | undefined {
    return this.getExtension(Insurance.Extension.PayorCode)?.valueString;
  }

  get startDate(): string | undefined {
    return this.resource.period?.start;
  }

  get endDate(): string | undefined {
    return this.resource.period?.end;
  }

  get documentReferenceId(): string | undefined {
    const identifier = this.resource.identifier?.find(
      (id) => id.system === Insurance.Identifier.DocumentReference,
    );
    return identifier?.value?.replace("DocumentReference/", "");
  }

  get order(): number | undefined {
    return this.resource.order;
  }

  get lastUpdated(): string | undefined {
    return this.resource.meta?.lastUpdated;
  }

  get isActive(): boolean {
    return this.resource.status === "active";
  }

  get patientReference(): Reference<Patient> {
    return this.resource.beneficiary;
  }

  //------------------------------------------------------------------------------------------------
  //                                      INSTANCE METHODS
  //------------------------------------------------------------------------------------------------
  /**
   * Converts domain input to a FHIR Coverage for creation.
   */
  static toFhirCreateObject(request: InsuranceUpsertRequest): ResourceData<Coverage> {
    return getFhirCoverage(request);
  }

  /**
   * Converts domain input to a FHIR Coverage for updates.
   */
  static toFhirUpdateObject(request: InsuranceUpsertRequest): ResourceData<Coverage> {
    const coverage = getFhirCoverage(request);
    return {
      ...coverage,
      extension: [
        {
          url: Insurance.Extension.PayorCode,
          valueString: request.payorCode || null,
        },
      ],
    };
  }

  /**
   * Builds a Coverage create-draft body with the same create-time `meta` / compartment prep as a
   * real create, for document-harness batch POST drafts (no persist; harness provenance runs after).
   */
  static async prepareHarnessCreateDraft(
    medplum: MedplumClient,
    request: Omit<InsuranceUpsertRequest, "status">,
    options?: CreateResourceOptions,
  ): Promise<Coverage> {
    const fhirData = Insurance.toFhirCreateObject({
      ...request,
      status: "active",
      order: request.order ?? 1,
    });
    const prepared = await Insurance.prepareResourceForCreate(medplum, fhirData, options);
    return prepared as Coverage;
  }

  /**
   * Converts a domain query to FHIR search parameters.
   */
  static toFhirQuery(params: InsuranceSearchQuery): Record<string, string> {
    const query: Record<string, string> = {};
    if (params.memberId) {
      query.beneficiary = `Patient/${params.memberId}`;
    }
    if (params.status) {
      query.status = params.status;
    }
    return query;
  }

  static async create(
    medplum: MedplumClient,
    request: InsuranceUpsertRequest,
    options?: CreateResourceOptions,
  ): Promise<Insurance> {
    const {
      memberId,
      payorName,
      payorCode,
      planName,
      policyNumber,
      groupNumber,
      startDate,
      endDate,
      documentReferenceId,
    } = request;

    const resource = await Insurance.createResource(
      medplum,
      Insurance.toFhirCreateObject({
        memberId,
        status: "draft",
        policyNumber,
        payorName,
        payorCode,
        planName,
        groupNumber,
        startDate,
        endDate,
        documentReferenceId,
        order: request.order,
      }),
      options,
    );
    const insurance = new Insurance(resource);

    if (insurance.id && request.documentReferenceId) {
      await Insurance.ensureProvenance(
        medplum,
        {
          insuranceId: insurance.id,
          memberId: request.memberId,
          sources: [{ reference: `DocumentReference/${request.documentReferenceId}` }],
        },
        options,
      );
    }

    return insurance;
  }

  /**
   * Upserts the primary (order 1) active insurance.
   *
   * This method is intentionally restricted to primary insurance only.
   * Use `saveActive` for secondary or order-specific operations.
   */
  static async upsertActive(
    medplum: MedplumClient,
    request: Omit<InsuranceUpsertRequest, "status">,
    options?: CreateResourceOptions,
  ): Promise<Insurance> {
    if (request.order && request.order !== 1) {
      throw new Error(
        "upsertActive is restricted to primary insurance (order 1). Use saveActive for other slots.",
      );
    }

    const {
      memberId,
      payorName,
      payorCode,
      planName,
      policyNumber,
      groupNumber,
      startDate,
      endDate,
      documentReferenceId,
    } = request;

    const activeRecords = await Insurance.searchActive(medplum, { memberId });
    const existing = Insurance.selectPrimaryFromList(activeRecords);

    if (existing) {
      const resource = await Insurance.patchResource(medplum, {
        id: existing.id,
        patch: Insurance.toFhirUpdateObject({
          memberId,
          status: "active",
          policyNumber,
          payorName,
          payorCode,
          planName,
          groupNumber,
          startDate,
          endDate,
          documentReferenceId,
          order: 1,
        }),
      });
      return new Insurance(resource);
    }

    const resource = await Insurance.createResource(
      medplum,
      Insurance.toFhirCreateObject({
        memberId,
        status: "active",
        policyNumber,
        payorName,
        payorCode,
        planName,
        groupNumber,
        startDate,
        endDate,
        documentReferenceId,
        order: 1,
      }),
      options,
    );
    const insurance = new Insurance(resource);

    if (insurance.id && documentReferenceId) {
      await Insurance.ensureProvenance(
        medplum,
        {
          insuranceId: insurance.id,
          memberId,
          sources: [{ reference: `DocumentReference/${documentReferenceId}` }],
        },
        options,
      );
    }

    return insurance;
  }

  /**
   * Creates an active insurance without overwriting existing records.
   */
  static async createActive(
    medplum: MedplumClient,
    request: Omit<InsuranceUpsertRequest, "status">,
  ): Promise<Insurance> {
    const resource = await Insurance.createResource(
      medplum,
      Insurance.toFhirCreateObject({
        ...request,
        status: "active",
        order: request.order ?? 1,
      }),
    );
    return new Insurance(resource);
  }

  /**
   * Saves an active insurance by id, or creates a new one when missing.
   *
   * When no `insuranceId` is provided, searches for an existing active record
   * with the same member + order before creating. This prevents duplicate
   * Coverage resources from overlapping debounced saves or error-recovery retries.
   */
  static async saveActive(
    medplum: MedplumClient,
    request: InsuranceSaveRequest,
  ): Promise<Insurance> {
    const { insuranceId, ...payload } = request;
    const order = payload.order ?? 1;

    // known record -- patch directly
    if (insuranceId) {
      const resource = await Insurance.patchResource(medplum, {
        id: insuranceId,
        patch: Insurance.toFhirUpdateObject({ ...payload, status: "active", order }),
      });
      return new Insurance(resource);
    }

    // no id -- guard against duplicate creates by checking for an existing
    // active record in the same member + order slot
    const existing = await Insurance.searchActive(medplum, { memberId: payload.memberId });
    const match =
      order === 1
        ? Insurance.selectPrimaryFromList(existing)
        : existing.find((ins) => ins.order === order);

    if (match) {
      const resource = await Insurance.patchResource(medplum, {
        id: match.id,
        patch: Insurance.toFhirUpdateObject({ ...payload, status: "active", order }),
      });
      return new Insurance(resource);
    }

    return Insurance.createActive(medplum, payload);
  }

  /**
   * Get an insurance by id.
   *
   * @param medplum - The Medplum client.
   * @param insuranceId - The id of the Insurance.
   *
   * @returns The insurance.
   * @throws Error if the insurance is not found.
   */
  /**
   * Stores a Junction payor code that was resolved unambiguously at order time.
   *
   * Junction resolves the payor code from the payor *name* on every lab-order page load and then
   * discards it, so an entry saved without a code stays codeless forever and re-resolves from
   * scratch every time (ENG-5402). Persisting the code lets a repairable entry heal itself on next
   * use, and is what makes the "this company will not match" warning on the insurance form
   * trustworthy rather than noise.
   *
   * **Only call this for an unambiguous match** — one Junction result for the name. A fuzzy match
   * or a first-of-many fallback is a guess about which carrier to bill, and writing a guess into
   * the chart turns a recoverable per-order decision into permanent billing data.
   *
   * Patches only the payor-code extension and preserves every other extension, so it cannot
   * clobber a field it does not know about. No-ops when a code is already stored.
   */
  static async storeResolvedPayorCode(
    medplum: MedplumClient,
    insuranceId: string,
    payorCode: string,
  ): Promise<void> {
    const insurance = await Insurance.getById(medplum, insuranceId);
    if (insurance.payorCode) {
      return;
    }

    const otherExtensions = (insurance.resource.extension ?? []).filter(
      (extension) => extension.url !== Insurance.Extension.PayorCode,
    );

    await Insurance.patchResource(medplum, {
      id: insuranceId,
      patch: {
        extension: [
          ...otherExtensions,
          { url: Insurance.Extension.PayorCode, valueString: payorCode },
        ],
      },
    });
  }

  static async getById(medplum: MedplumClient, insuranceId: string): Promise<Insurance> {
    const coverage = await Insurance.searchOneResource(medplum, { _id: insuranceId });
    if (!coverage) {
      throw new Error("Insurance not found");
    }
    return new Insurance(coverage);
  }

  /**
   * Search for an insurance by member id.
   *
   * @param medplum - The Medplum client.
   * @param searchQuery - The search query.
   *
   * @returns The insurance, or undefined if no insurance is found.
   */
  static async searchOneWithQuery(
    medplum: MedplumClient,
    searchQuery: InsuranceSearchQuery,
  ): Promise<Insurance | undefined> {
    if (!searchQuery.memberId) {
      return undefined;
    }
    if (searchQuery.status) {
      return Insurance.searchPrimary(medplum, searchQuery);
    }

    const primaryActive = await Insurance.searchPrimary(medplum, {
      memberId: searchQuery.memberId,
      status: "active",
    });
    if (primaryActive) {
      return primaryActive;
    }

    return Insurance.searchPrimary(medplum, searchQuery);
  }

  /**
   * Search for insurance records by member id.
   *
   * @param medplum - The Medplum client.
   * @param searchQuery - The search query.
   *
   * @returns The list of insurance records.
   */
  static async search(
    medplum: MedplumClient,
    params: InsuranceSearchQuery,
    options?: SearchOptions,
  ): Promise<Insurance[]> {
    if (!params.memberId) {
      return [];
    }

    const resources = await Insurance.searchResources(
      medplum,
      Insurance.toFhirQuery(params),
      options,
    );
    return resources.map((resource) => new Insurance(resource));
  }

  /**
   * Searches for active insurance records by member id.
   */
  static async searchActive(
    medplum: MedplumClient,
    params: Omit<InsuranceSearchQuery, "status">,
    options?: SearchOptions,
  ): Promise<Insurance[]> {
    return Insurance.search(medplum, { ...params, status: "active" }, options);
  }

  /**
   * Searches for the primary insurance record.
   */
  static async searchPrimary(
    medplum: MedplumClient,
    params: InsuranceSearchQuery,
  ): Promise<Insurance | undefined> {
    if (!params.memberId) {
      return undefined;
    }
    const resources = await Insurance.searchResources(medplum, Insurance.toFhirQuery(params), {
      limit: 100,
    });
    const insurances = resources.map((resource) => new Insurance(resource));
    return Insurance.selectPrimaryFromList(insurances);
  }

  /**
   * Selects the primary insurance from a list using explicit signals only.
   *
   * Returns the record with `order === 1`, or the sole record when only one
   * exists (unambiguous). Returns `undefined` when primary can't be
   * determined — callers must handle missing primary gracefully.
   */
  static selectPrimaryFromList(insurances: Insurance[]): Insurance | undefined {
    if (insurances.length === 0) return undefined;

    // explicit primary
    const primaryByOrder = insurances.find((ins) => ins.order === 1);
    if (primaryByOrder) return primaryByOrder;

    // single record — unambiguously primary
    if (insurances.length === 1) return insurances[0];

    // multiple records without explicit order — we can't determine primary
    return undefined;
  }

  /**
   * Searches for insurance records by document reference ID.
   *
   * @param medplum - The medplum client.
   * @param documentReferenceId - The document reference ID.
   * @returns The insurance records associated with the specified document.
   */
  static async searchByDocumentReferenceId(medplum: MedplumClient, documentReferenceId: string) {
    const resources = await searchResourcesByDocumentReference<Coverage>({
      medplum,
      model: Insurance,
      documentReferenceId,
      legacyQuery: {
        identifier: `${Insurance.Identifier.DocumentReference}|DocumentReference/${documentReferenceId}`,
      },
    });

    return resources.map((resource) => new Insurance(resource));
  }

  /**
   * Subscribe to insurance records matching a query.
   */
  static subscribe(
    medplum: MedplumClient,
    query: InsuranceSearchQuery,
    callback: SubscriptionCallback<Insurance>,
  ) {
    return Insurance.subscribeToResource(medplum, Insurance.toFhirQuery(query), (resource, meta) =>
      callback(new Insurance(resource), meta),
    );
  }
  /**
   * Ensures a Provenance resource exists for this insurance record, idempotently adding sources.
   *
   * @param medplum - The Medplum client.
   * @param args - Provenance configuration.
   */
  static async ensureProvenance(
    medplum: MedplumClient,
    args: {
      insuranceId: string;
      memberId: string;
      sources: { reference: string }[];
    },
    options?: CreateResourceOptions,
  ): Promise<void> {
    const { insuranceId, memberId, sources } = args;

    if (sources.length === 0) {
      logger.warn("No sources provided for Provenance", { insuranceId });
      return;
    }

    try {
      const hasSurveyResponse = sources.some((source) =>
        source.reference?.startsWith("QuestionnaireResponse/"),
      );

      await Source.create(
        medplum,
        {
          for: { reference: `${Insurance.ResourceType}/${insuranceId}` },
          date: time().toISOString(),
          author: hasSurveyResponse ? { reference: `Patient/${memberId}` } : undefined,
          assembler: hasSurveyResponse
            ? undefined
            : { reference: `Organization/${getVibrantSuperOrgId()}` },
          sources,
        },
        options,
      );
    } catch (error) {
      logger.error("Error ensuring Provenance for insurance", { insuranceId, error });
      // Don't throw - provenance is additive metadata.
    }
  }

  //------------------------------------------------------------------------------------------------
  //                                      INSTANCE METHODS
  //------------------------------------------------------------------------------------------------
  /**
   * Returns the FHIR resource for the insurance.
   */
  toFhirResource(): Coverage {
    return this.resource;
  }
}

function getFhirCoverage(request: InsuranceUpsertRequest): ResourceData<Coverage> {
  const {
    memberId,
    status,
    policyNumber,
    payorName,
    payorCode,
    planName,
    groupNumber,
    startDate,
    endDate,
    documentReferenceId,
    order,
  } = request;

  // Coverage.payor is required (FHIR R4 1..1). Never write a payor-less/blank
  // Coverage: legacy skeleton rows without a payor were structurally invalid and
  // rejected by validated writes / Patient/$set-accounts (ENG-4800).
  if (!payorName?.trim().length) {
    throw new Error("Coverage requires a non-empty payor name");
  }

  assertCoverageDatesInRange({ startDate, endDate });

  const classArray = [
    ...(planName
      ? [
          {
            type: {
              coding: [
                {
                  system: "http://terminology.hl7.org/CodeSystem/coverage-class",
                  code: "plan",
                },
              ],
            },
            value: planName,
            name: planName,
          },
        ]
      : []),
    ...(groupNumber
      ? [
          {
            type: {
              coding: [
                {
                  system: "http://terminology.hl7.org/CodeSystem/coverage-class",
                  code: "group",
                },
              ],
            },
            value: groupNumber,
          },
        ]
      : []),
  ];

  const extensionArray = [
    ...(payorCode
      ? [
          {
            url: Insurance.Extension.PayorCode,
            valueString: payorCode,
          },
        ]
      : []),
  ];

  return {
    status,
    beneficiary: { reference: `Patient/${memberId}` },
    subscriber: { reference: `Patient/${memberId}` },
    subscriberId: policyNumber,
    ...(startDate || endDate ? { period: { start: startDate, end: endDate } } : {}),
    ...(order ? { order } : {}),
    payor: [{ display: payorName }],
    ...(classArray.length > 0 ? { class: classArray } : {}),
    identifier: [
      {
        type: {
          coding: [
            {
              system: "http://terminology.hl7.org/CodeSystem/v2-0203",
              code: "MB",
              display: "Member Number",
            },
          ],
        },
        value: policyNumber,
        system: Insurance.Identifier.PolicyNumber,
      },
      ...(documentReferenceId
        ? [
            {
              system: Insurance.Identifier.DocumentReference,
              value: `DocumentReference/${documentReferenceId}`,
            },
          ]
        : []),
    ],
    ...(extensionArray.length > 0 ? { extension: extensionArray } : {}),
  };
}
